import LanguageSelector from "../common/LanguageSelector";
import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { USER_LOADED } from "../../actions/types";
import "../../assets/css/setup.css";

// ── Step config ──────────────────────────────────────────────────────────────

const STEP_INDUSTRY = {
  key: "industry",
  title: "What's your industry?",
  subtitle: "We'll tailor the experience to your field.",
  options: [
    {
      value: "agency",
      label: "Agency",
      icon: (
        <svg viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>
      ),
    },
    {
      value: "consultancy",
      label: "Consultancy",
      icon: (
        <svg viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
      ),
    },
    {
      value: "construction",
      label: "Construction",
      icon: (
        <svg viewBox="0 0 24 24"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>
      ),
    },
    {
      value: "retail",
      label: "Retail",
      icon: (
        <svg viewBox="0 0 24 24"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
      ),
    },
    {
      value: "other",
      label: "Other",
      icon: (
        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
      ),
    },
  ],
};

const STEP_TEAM_USE = {
  key: "team_use",
  title: "How will you use Minutezilla?",
  subtitle: "This helps us show you the right features.",
  options: [
    {
      value: "self",
      label: "Just me",
      icon: (
        <svg viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
      ),
    },
    {
      value: "team",
      label: "My team",
      icon: (
        <svg viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
      ),
    },
    {
      value: "clients",
      label: "For clients",
      icon: (
        <svg viewBox="0 0 24 24"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
      ),
    },
  ],
};

const STEP_TEAM_SIZE = {
  key: "team_size",
  title: "How large is your team?",
  subtitle: "We'll show only what's relevant to your setup.",
  options: [
    {
      value: "solo",
      label: "Solo",
      icon: (
        <svg viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
      ),
    },
    {
      value: "2-3",
      label: "2–3 people",
      icon: (
        <svg viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
      ),
    },
    {
      value: "4-10",
      label: "4–10 people",
      icon: (
        <svg viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>
      ),
    },
    {
      value: "10+",
      label: "10+ people",
      icon: (
        <svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="4"/><path d="M2 20s2-4 10-4 10 4 10 4"/><path d="M5 14s1-2 5-2"/><path d="M19 14s-1-2-5-2"/></svg>
      ),
    },
  ],
};

const STEPS = [STEP_INDUSTRY, STEP_TEAM_USE, STEP_TEAM_SIZE];

// ── Component ────────────────────────────────────────────────────────────────

export default function SetupWizard() {
  useTranslation();
  const auth    = useSelector((state) => state.auth);
  const history = useNavigate();
  const dispatch = useDispatch();

  const [step, setStep]       = useState(0);
  const [answers, setAnswers] = useState({ industry: "", team_use: "", team_size: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  // Guards
  if (!auth.isAuthenticated) {
    history("/login");
    return null;
  }
  if (auth.onboarding_complete === true) {
    history("/dashboard");
    return null;
  }

  const current = STEPS[step];
  const selected = answers[current.key];

  const select = (value) => {
    setAnswers((prev) => ({ ...prev, [current.key]: value }));
    setError("");
  };

  const next = async () => {
    if (!selected) {
      setError(tr("Please pick an option to continue."));
      return;
    }

    if (step < STEPS.length - 1) {
      setStep((s) => s + 1);
      return;
    }

    // Final step — submit
    setLoading(true);
    setError("");
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${auth.token}`,
          "Content-Type": "application/json",
        },
      };
      await axios.put(
        "/api/workspace/onboarding",
        JSON.stringify({
          workspace_id: auth.workspace_id,
          industry    : answers.industry,
          team_use    : answers.team_use,
          team_size   : answers.team_size,
        }),
        config
      );

      dispatch({
        type   : USER_LOADED,
        payload: {
          onboarding_complete: true,
          team_size          : answers.team_size,
          industry           : answers.industry,
        },
      });

      history("/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.errors?.[0]?.msg || tr("Something went wrong. Please try again.");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const isLast = step === STEPS.length - 1;

  return (
    <div className="setup-root">
      <div className="setup-card">
        <div className="setup-logo">Minutezilla</div>

        {/* Progress indicator */}
        <div className="setup-progress">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.key}>
              <div className={`setup-step-dot ${i < step ? "done" : i === step ? "active" : ""}`}>
                {i < step ? (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <polyline points="2 6 5 9 10 3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  i + 1
                )}
              </div>
              {i < STEPS.length - 1 && (
                <div className={`setup-step-line ${i < step ? "done" : ""}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <LanguageSelector />
        {/* Step header */}
        <div className="setup-header">
          <h2>{tr(current.title)}</h2>
          <p>{tr(current.subtitle)}</p>
        </div>

        {/* Options */}
        <div className={`setup-grid ${current.options.length === 3 ? "cols-3" : ""}`}>
          {current.options.map((opt) => (
            <button
              key={opt.value}
              className={`setup-option ${selected === opt.value ? "selected" : ""}`}
              onClick={() => select(opt.value)}
            >
              <div className="setup-option-icon">{opt.icon}</div>
              <span className="setup-option-label">{tr(opt.label)}</span>
            </button>
          ))}
        </div>

        {error && <div className="setup-error">{error}</div>}

        <div className="setup-actions">
          <button className="setup-btn" onClick={next} disabled={loading}>
            {loading ? tr("Setting up…") : isLast ? tr("Get started") : tr("Continue →")}
          </button>
        </div>
      </div>
    </div>
  );
}
